import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .fra * { box-sizing:border-box; }
  .fra { font-family:'Sora',sans-serif; color:#1a1d2e; animation:fraIn .35s ease both; }
  @keyframes fraIn { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
  .fra-hdr { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .fra-title { font-size:1.35rem; font-weight:700; color:#0f1123; letter-spacing:-.02em; }
  .fra-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-green { background:#10c98e; color:#fff; } .btn-green:hover { background:#0db87e; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; } .btn-red:hover { background:#ef4444; color:white; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; } .btn-ghost:hover { background:#e4e7f0; }
  .btn-sm { padding:6px 11px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; }
  .tabs { display:flex; gap:4px; background:#f0f2f7; border-radius:11px; padding:4px; margin-bottom:1.2rem; width:fit-content; flex-wrap:wrap; }
  .tab { padding:6px 18px; border-radius:8px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; background:transparent; color:#9094a8; }
  .tab.on { background:white; color:#0f1123; box-shadow:0 1px 4px rgba(0,0,0,.1); }
  .stat-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.2rem; border:1px solid #e8ebf4; }
  .sc-label { font-size:.67rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .sc-value { font-size:1.4rem; font-weight:700; color:#0f1123; font-family:'JetBrains Mono',monospace; margin-top:2px; }
  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; }
  .tbl { width:100%; border-collapse:collapse; }
  .tbl thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .tbl th { padding:10px 16px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .tbl tbody tr { border-bottom:1px solid #f3f5fb; }
  .tbl tbody tr:hover { background:#fafbff; }
  .tbl td { padding:11px 16px; font-size:.8rem; vertical-align:middle; text-align:center !important; }
  .pill { display:inline-flex; font-size:.67rem; font-weight:600; padding:3px 9px; border-radius:99px; }
  .pill-pending  { background:#fffbeb; color:#f59e0b; }
  .pill-approved { background:#ecfdf5; color:#10c98e; }
  .pill-rejected { background:#fef2f2; color:#ef4444; }
  .type-pill { display:inline-flex; font-size:.67rem; font-weight:600; padding:2px 8px; border-radius:99px; }
  .type-biometric { background:#eef2ff; color:#3b6ef8; }
  .type-other     { background:#f0f2f7; color:#6b7194; }
  .empty { text-align:center; padding:3rem 2rem; color:#9094a8; font-size:.82rem; }
  .loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spin { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:sp .7s linear infinite; }
  @keyframes sp { to{transform:rotate(360deg)} }
  .mono { font-family:'JetBrains Mono',monospace; font-size:.76rem; color:#6b7194; }
  .mb { position:fixed; inset:0; background:rgba(10,12,28,.65); backdrop-filter:blur(5px); display:flex; align-items:center; justify-content:center; z-index:9999; padding:20px; }
  .mo { background:white; border-radius:20px; width:100%; max-width:500px; max-height:90vh; display:flex; flex-direction:column; box-shadow:0 25px 60px rgba(0,0,0,.25); animation:moIn .25s ease; overflow:hidden; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .mo-hd { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .mo-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .mo-x { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .mo-x svg { width:14px; height:14px; stroke-width:2; }
  .mo-bd { padding:20px 22px; flex:1; overflow-y:auto; }
  .mo-ft { display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; }
  .ff { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .fl { font-size:.7rem; font-weight:700; color:#6b7194; text-transform:uppercase; letter-spacing:.06em; }
  .ft { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; resize:vertical; min-height:80px; }
  .ft:focus { border-color:#3b6ef8; background:white; }
  .payload-box { background:#f8f9fc; border:1px solid #e8ebf4; border-radius:10px; padding:12px; font-size:.76rem; color:#4a5075; margin-bottom:14px; }
  .payload-box strong { color:#0f1123; display:inline-block; min-width:90px; }
  .payload-row { padding:3px 0; border-bottom:1px solid #f0f2f8; display:flex; gap:8px; }
  .payload-row:last-child { border-bottom:none; }
  .biometric-badge { display:inline-flex; font-size:.67rem; font-weight:600; padding:2px 8px; border-radius:99px; background:#eef2ff; color:#3b6ef8; margin-left:6px; vertical-align:middle; }

  .fra-cards { display:none; padding:10px; }
  .fra-card { background:white; border-radius:12px; border:1px solid #e8ebf4; padding:13px; margin-bottom:8px; }
  .fra-card-top { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; margin-bottom:8px; }
  .fra-card-type { font-size:.84rem; font-weight:700; color:#0f1123; }
  .fra-card-by { font-size:.72rem; color:#9094a8; margin-top:2px; }
  .fra-card-row { display:flex; justify-content:space-between; align-items:flex-start; font-size:.78rem; padding:4px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .fra-card-row:last-of-type { border-bottom:none; }
  .fra-card-label { color:#9094a8; font-weight:600; flex-shrink:0; }
  .fra-card-val { text-align:right; }
  .fra-card-actions { display:flex; gap:6px; flex-wrap:wrap; margin-top:8px; }

  @media (max-width:768px) {
    .stat-grid { grid-template-columns:1fr 1fr; }
    .fra-title { font-size:1.1rem; }
    .tbl-wrap { overflow-x:auto; display:none !important; }
    .fra-cards { display:block; }
  }
`;

const TYPE_LABEL = {
  fee_action:           'Fee Action',
  fee_mark_paid:        'Fee — Mark Paid',
  fee_add_fine:         'Fee — Add Fine',
  fee_discount:         'Fee — Discount',
  fee_edit:             'Fee — Edit Record',
  fee_bulk_generate:    'Fee — Bulk Generate',
  staff_leave_add:      'Leave — Add',
  staff_leave_review:   'Leave — Review',
  expense_add:          'Add Expense',
  expense_edit:         'Edit Expense',
  expense_delete:       'Delete Expense',
  asset_add:            'Add Asset',
  asset_edit:           'Edit Asset',
  asset_delete:         'Delete Asset',
  asset_maintenance:    'Asset Maintenance',
  salary_action:        'Salary Action',
  letter_character_certificate: 'Character Certificate',
  letter_school_leaving:        'School Leaving Cert',
  letter_experience_letter:     'Experience Letter',
  letter_appointment_letter:    'Appointment Letter',
  // ── Biometric ───────────────────────────────────────────────
  rfid_assign_teacher:  'RFID — Assign Teacher',
  rfid_assign_student:  'RFID — Assign Student',
  biometric_config:     'Biometric Config Change',
  student_gate_entry:   'Manual Gate Entry',
  // ── Staff Attendance ────────────────────────────────────────
  staff_attendance_mark:'Staff Attendance',
  // ── Staff Management ─────────────────────────────────────────
  staff_add:            'Staff — Add',
  staff_edit:           'Staff — Edit',
  staff_toggle:         'Staff — Activate/Deactivate',
  staff_exit:           'Staff — Exit',
  staff_reactivate:     'Staff — Reactivate',
};

const BIOMETRIC_TYPES = ['rfid_assign_teacher', 'rfid_assign_student', 'biometric_config', 'student_gate_entry'];

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2,'0')}-${String(dt.getMonth()+1).padStart(2,'0')}-${dt.getFullYear()}`;
};

// camelCase key → "Title Case With Spaces" label, for the generic fallback below
const humanizeKey = (k) => k
  .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
  .replace(/^_+/, '')
  .replace(/\b\w/g, c => c.toUpperCase());

// Render payload in a human-readable way based on request type
const PayloadView = ({ type, payload }) => {
  if (!payload) return null;
  const p = payload;

  if (type === 'rfid_assign_teacher') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Teacher:</strong> {p.teacherName || '—'}</div>
      <div className="payload-row"><strong>Employee ID:</strong> <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{p.employeeId || '—'}</span></div>
      <div className="payload-row"><strong>New RFID Card:</strong> <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600,color:'#3b6ef8'}}>{p.rfidCard}</span></div>
    </div>
  );

  if (type === 'rfid_assign_student') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Student:</strong> {p.studentName || '—'}</div>
      <div className="payload-row"><strong>Admission No:</strong> <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{p.admissionNo || '—'}</span></div>
      <div className="payload-row"><strong>Class:</strong> {p.className} {p.section}</div>
      <div className="payload-row"><strong>New RFID Card:</strong> <span style={{fontFamily:"'JetBrains Mono',monospace",fontWeight:600,color:'#3b6ef8'}}>{p.rfidCard}</span></div>
    </div>
  );

  if (type === 'staff_attendance_mark') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Staff:</strong> {p.staffName || '—'}{p.employeeId ? ` (${p.employeeId})` : ''}</div>
      <div className="payload-row"><strong>Date:</strong> {p.date}</div>
      <div className="payload-row"><strong>Status:</strong> <span style={{textTransform:'capitalize'}}>{p.status}</span></div>
      {p.checkIn  && <div className="payload-row"><strong>Check In:</strong>  {new Date(p.checkIn).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true})}</div>}
      {p.checkOut && <div className="payload-row"><strong>Check Out:</strong> {new Date(p.checkOut).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true})}</div>}
      {(p.lateMinutes||0) > 0 && <div className="payload-row"><strong>Late:</strong> {p.lateMinutes} min</div>}
    </div>
  );

  if (type === 'fee_mark_paid') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Student:</strong> {p.studentName || '—'} {p.admissionNo ? `(${p.admissionNo})` : ''}</div>
      <div className="payload-row"><strong>Month:</strong> {p.month}</div>
      <div className="payload-row"><strong>Amount Paid:</strong> Rs. {Number(p.amountPaid||0).toLocaleString('en-PK')}</div>
      <div className="payload-row"><strong>Method:</strong> <span style={{textTransform:'capitalize'}}>{(p.paymentMethod||'').replace('_',' ')}</span></div>
      {p.transactionId && <div className="payload-row"><strong>Transaction ID:</strong> {p.transactionId}</div>}
    </div>
  );

  if (type === 'fee_add_fine') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Student:</strong> {p.studentName || '—'} {p.admissionNo ? `(${p.admissionNo})` : ''}</div>
      <div className="payload-row"><strong>Month:</strong> {p.month}</div>
      <div className="payload-row"><strong>Fine Amount:</strong> Rs. {Number(p.fineAmount||0).toLocaleString('en-PK')}</div>
      <div className="payload-row"><strong>Reason:</strong> {p.fineReason || '—'}</div>
    </div>
  );

  if (type === 'fee_discount') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Student:</strong> {p.studentName || '—'} {p.admissionNo ? `(${p.admissionNo})` : ''}</div>
      <div className="payload-row"><strong>Month:</strong> {p.month}</div>
      <div className="payload-row"><strong>Discount Amount:</strong> Rs. {Number(p.discountAmount||0).toLocaleString('en-PK')}</div>
      <div className="payload-row"><strong>Reason:</strong> {p.discountReason || '—'}</div>
    </div>
  );

  if (type === 'fee_edit') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Student:</strong> {p.studentName || '—'} {p.admissionNo ? `(${p.admissionNo})` : ''}</div>
      <div className="payload-row"><strong>Month:</strong> {p.month}</div>
      <div className="payload-row"><strong>Fee Amount:</strong> Rs. {Number(p.feeAmount||0).toLocaleString('en-PK')}</div>
      <div className="payload-row"><strong>Due Date:</strong> {p.dueDate}</div>
      <div className="payload-row"><strong>Status:</strong> <span style={{textTransform:'capitalize'}}>{p.status}</span></div>
      {p.notes && <div className="payload-row"><strong>Notes:</strong> {p.notes}</div>}
    </div>
  );

  if (type === 'fee_bulk_generate') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Month:</strong> {p.month}</div>
      <div className="payload-row"><strong>Section:</strong> {p.section || 'All sections'}</div>
      <div className="payload-row"><strong>Fee Amount:</strong> {p.feeAmount ? `Rs. ${Number(p.feeAmount).toLocaleString('en-PK')}` : 'Use class default'}</div>
      <div className="payload-row"><strong>Due Date:</strong> {p.dueDate || 'Auto (school rules)'}</div>
    </div>
  );

  if (type === 'staff_leave_add') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Staff:</strong> {p.staffName || '—'} <span style={{textTransform:'capitalize'}}>({p.applicantRole})</span></div>
      <div className="payload-row"><strong>Type:</strong> <span style={{textTransform:'capitalize'}}>{p.type}</span></div>
      <div className="payload-row"><strong>From:</strong> {p.startDate}</div>
      <div className="payload-row"><strong>To:</strong> {p.endDate}</div>
      {p.reason && <div className="payload-row"><strong>Reason:</strong> {p.reason}</div>}
      {p.documentUrl && <div className="payload-row"><strong>Document:</strong> <a href={p.documentUrl} target="_blank" rel="noreferrer" style={{color:'#3b6ef8'}}>View</a></div>}
    </div>
  );

  if (type === 'staff_leave_review') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Staff:</strong> {p.staffName || '—'}</div>
      <div className="payload-row"><strong>Decision:</strong> <span style={{textTransform:'capitalize'}}>{(p.status||'').replace(/-/g,' ')}</span></div>
      {p.adminNote && <div className="payload-row"><strong>Note:</strong> {p.adminNote}</div>}
    </div>
  );

  if (type === 'salary_action') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Staff:</strong> {p.teacherName || '—'}</div>
      <div className="payload-row"><strong>Month:</strong> {p.month}</div>
      <div className="payload-row"><strong>Action:</strong> <span style={{textTransform:'capitalize'}}>{(p.action||'').replace(/_/g,' ')}</span></div>
      {p.action === 'add_attendance' && p.attendance && (
        <>
          <div className="payload-row"><strong>Date:</strong> {p.attendance.date}</div>
          <div className="payload-row"><strong>Status:</strong> <span style={{textTransform:'capitalize'}}>{p.attendance.status}</span></div>
          {p.attendance.checkIn  && <div className="payload-row"><strong>Check In:</strong> {p.attendance.checkIn}</div>}
          {p.attendance.checkOut && <div className="payload-row"><strong>Check Out:</strong> {p.attendance.checkOut}</div>}
          {Number(p.attendance.lateMinutes||0)      > 0 && <div className="payload-row"><strong>Late:</strong> {p.attendance.lateMinutes} min</div>}
          {Number(p.attendance.earlyExitMinutes||0) > 0 && <div className="payload-row"><strong>Early Exit:</strong> {p.attendance.earlyExitMinutes} min</div>}
        </>
      )}
    </div>
  );

  if (type === 'biometric_config') return (
    <div className="payload-box">
      <div className="payload-row"><strong>New IP:</strong> <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{p.ip}</span></div>
      <div className="payload-row"><strong>Port:</strong> <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{p.port || 4370}</span></div>
      <div className="payload-row"><strong>Poll Interval:</strong> <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{p.pollInterval || 60}s</span></div>
    </div>
  );

  if (type.startsWith('letter_')) return (
    <div className="payload-box">
      <div className="payload-row"><strong>Recipient:</strong> {p.targetName || '—'}</div>
      <div className="payload-row"><strong>Letter Type:</strong> <span style={{textTransform:'capitalize'}}>{(p.letterType||'').replace(/_/g,' ')}</span></div>
    </div>
  );

  if (type === 'staff_add' || type === 'staff_edit') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Name:</strong> {p.name || '—'}</div>
      <div className="payload-row"><strong>Department:</strong> {p.department || '—'}</div>
      <div className="payload-row"><strong>Designation:</strong> {p.designation || '—'}</div>
      <div className="payload-row"><strong>Employee ID:</strong> {p.employeeId || '—'}</div>
      <div className="payload-row"><strong>Phone:</strong> {p.phone || '—'}</div>
      <div className="payload-row"><strong>Monthly Salary:</strong> {p.monthlySalary ? `Rs. ${Number(p.monthlySalary).toLocaleString('en-PK')}` : '—'}</div>
    </div>
  );

  if (type === 'staff_toggle') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Staff:</strong> {p.staffName || '—'}</div>
      <div className="payload-row"><strong>Current Status:</strong> <span style={{textTransform:'capitalize'}}>{p.currentStatus || '—'}</span></div>
    </div>
  );

  if (type === 'staff_exit') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Staff:</strong> {p.staffName || '—'}</div>
      <div className="payload-row"><strong>Exit Type:</strong> <span style={{textTransform:'capitalize'}}>{p.exitType || '—'}</span></div>
      <div className="payload-row"><strong>Exit Date:</strong> {p.exitDate || '—'}</div>
      <div className="payload-row"><strong>Note:</strong> {p.note || '—'}</div>
    </div>
  );

  if (type === 'staff_reactivate') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Staff:</strong> {p.staffName || '—'}</div>
    </div>
  );

  if (type === 'asset_maintenance') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Asset:</strong> {p.assetName || `#${p.assetId}`}</div>
      <div className="payload-row"><strong>Date:</strong> {p.date}</div>
      <div className="payload-row"><strong>Description:</strong> {p.description || '—'}</div>
      {Number(p.cost||0) > 0 && <div className="payload-row"><strong>Cost:</strong> Rs. {Number(p.cost).toLocaleString('en-PK')}</div>}
      {p.performedBy && <div className="payload-row"><strong>Performed By:</strong> {p.performedBy}</div>}
      {p.newCondition && <div className="payload-row"><strong>New Condition:</strong> <span style={{textTransform:'capitalize'}}>{p.newCondition}</span></div>}
    </div>
  );

  if (type === 'expense_add' || type === 'expense_edit') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Title:</strong> {p.title || '—'}</div>
      <div className="payload-row"><strong>Category:</strong> <span style={{textTransform:'capitalize'}}>{p.category}</span></div>
      {p.subcategory && <div className="payload-row"><strong>Sub-type:</strong> {p.subcategory}</div>}
      <div className="payload-row"><strong>Amount:</strong> Rs. {Number(p.amount||0).toLocaleString('en-PK')}</div>
      <div className="payload-row"><strong>Date:</strong> {p.date}</div>
      {p.description && <div className="payload-row"><strong>Description:</strong> {p.description}</div>}
      {p.vendor && <div className="payload-row"><strong>Vendor:</strong> {p.vendor}</div>}
      {p.receiptNo && <div className="payload-row"><strong>Receipt No:</strong> {p.receiptNo}</div>}
      <div className="payload-row"><strong>Payment Method:</strong> <span style={{textTransform:'capitalize'}}>{p.paymentMethod}</span></div>
      {p.previousReading && <div className="payload-row"><strong>Previous Reading:</strong> {p.previousReading}</div>}
      {p.meterReading && <div className="payload-row"><strong>Current Reading:</strong> {p.meterReading}</div>}
    </div>
  );

  if (type === 'expense_delete') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Expense:</strong> {p.title || `#${p._id || p.id}`}</div>
    </div>
  );

  if (type === 'asset_add' || type === 'asset_edit') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Name:</strong> {p.name || '—'}</div>
      <div className="payload-row"><strong>Category:</strong> <span style={{textTransform:'capitalize'}}>{p.category}</span></div>
      {p.subcategory && <div className="payload-row"><strong>Sub-type:</strong> {p.subcategory}</div>}
      {p.assetCode && <div className="payload-row"><strong>Asset Code:</strong> {p.assetCode}</div>}
      <div className="payload-row"><strong>Quantity:</strong> {p.quantity || 1}</div>
      {p.location && <div className="payload-row"><strong>Location:</strong> {p.location}</div>}
      <div className="payload-row"><strong>Condition:</strong> <span style={{textTransform:'capitalize'}}>{p.condition}</span></div>
      <div className="payload-row"><strong>Status:</strong> <span style={{textTransform:'capitalize'}}>{(p.status||'').replace('_',' ')}</span></div>
      {Number(p.purchasePrice||0) > 0 && <div className="payload-row"><strong>Purchase Price:</strong> Rs. {Number(p.purchasePrice).toLocaleString('en-PK')}</div>}
      {p.purchaseDate && <div className="payload-row"><strong>Purchase Date:</strong> {p.purchaseDate}</div>}
      {p.vendor && <div className="payload-row"><strong>Vendor:</strong> {p.vendor}</div>}
      {p.warrantyExpiry && <div className="payload-row"><strong>Warranty Expiry:</strong> {p.warrantyExpiry}</div>}
      {p.description && <div className="payload-row"><strong>Description:</strong> {p.description}</div>}
    </div>
  );

  if (type === 'asset_delete') return (
    <div className="payload-box">
      <div className="payload-row"><strong>Asset:</strong> {p.name || `#${p._id || p.id}`}</div>
    </div>
  );

  // Default — show all key/value pairs, with camelCase keys turned into proper labels
  return (
    <div className="payload-box">
      {Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => (
        <div key={k} className="payload-row">
          <strong>{humanizeKey(k)}:</strong> {typeof v === 'object' ? JSON.stringify(v) : String(v)}
        </div>
      ))}
    </div>
  );
};

const RequestSummary = ({ r, isBiometric }) => (
  <>
    {r.type === 'rfid_assign_teacher' && r.payload?.teacherName && (
      <span>{r.payload.teacherName} → <span style={{fontFamily:"'JetBrains Mono',monospace",color:'#3b6ef8'}}>{r.payload.rfidCard}</span></span>
    )}
    {r.type === 'rfid_assign_student' && r.payload?.studentName && (
      <span>{r.payload.studentName} → <span style={{fontFamily:"'JetBrains Mono',monospace",color:'#3b6ef8'}}>{r.payload.rfidCard}</span></span>
    )}
    {r.type === 'biometric_config' && r.payload?.ip && (
      <span style={{fontFamily:"'JetBrains Mono',monospace"}}>{r.payload.ip}:{r.payload.port}</span>
    )}
    {r.type === 'staff_attendance_mark' && r.payload?.staffName && (
      <span>{r.payload.staffName} → <span style={{fontWeight:600,textTransform:'capitalize'}}>{r.payload.status}</span> ({r.payload.date})</span>
    )}
    {r.type === 'salary_action' && r.payload?.teacherName && (
      <span>{r.payload.teacherName} — <span style={{fontWeight:600,textTransform:'capitalize'}}>{(r.payload.action||'').replace(/_/g,' ')}</span> ({r.payload.month})</span>
    )}
    {['fee_mark_paid','fee_add_fine','fee_discount','fee_edit'].includes(r.type) && r.payload?.studentName && (
      <span>{r.payload.studentName} ({r.payload.month})</span>
    )}
    {r.type === 'fee_bulk_generate' && (
      <span>{r.payload?.month} — {r.payload?.section || 'all sections'}</span>
    )}
    {r.type === 'staff_leave_add' && (
      <span>{r.payload?.staffName} — <span style={{textTransform:'capitalize'}}>{r.payload?.type}</span> ({r.payload?.startDate} → {r.payload?.endDate})</span>
    )}
    {r.type === 'staff_leave_review' && (
      <span>{r.payload?.staffName} → <span style={{fontWeight:600,textTransform:'capitalize'}}>{(r.payload?.status||'').replace(/-/g,' ')}</span></span>
    )}
    {(r.type === 'staff_add' || r.type === 'staff_edit') && (
      <span>{r.payload?.name || '—'} — {r.payload?.designation || '—'}</span>
    )}
    {r.type === 'staff_toggle' && (
      <span>{r.payload?.staffName || '—'} — <span style={{textTransform:'capitalize'}}>{r.payload?.currentStatus}</span></span>
    )}
    {r.type === 'staff_exit' && (
      <span>{r.payload?.staffName || '—'} — <span style={{textTransform:'capitalize'}}>{r.payload?.exitType}</span></span>
    )}
    {r.type === 'staff_reactivate' && (
      <span>{r.payload?.staffName || '—'}</span>
    )}
    {r.type.startsWith('letter_') && (
      <span>{r.payload?.targetName || '—'}</span>
    )}
    {(r.type === 'expense_add' || r.type === 'expense_edit') && (
      <span>{r.payload?.title || '—'} — <span style={{fontWeight:600}}>Rs. {Number(r.payload?.amount||0).toLocaleString('en-PK')}</span>{r.payload?.vendor ? ` (${r.payload.vendor})` : ''}</span>
    )}
    {r.type === 'expense_delete' && (
      <span>{r.payload?.title || `#${r.payload?._id || r.payload?.id}`}</span>
    )}
    {(r.type === 'asset_add' || r.type === 'asset_edit') && (
      <span>{r.payload?.name || '—'} — <span style={{textTransform:'capitalize'}}>{r.payload?.category}</span>{r.payload?.quantity ? ` × ${r.payload.quantity}` : ''}</span>
    )}
    {r.type === 'asset_delete' && (
      <span>{r.payload?.name || `#${r.payload?.id}`}</span>
    )}
    {r.type === 'asset_maintenance' && (
      <span>{r.payload?.assetName || `#${r.payload?.assetId}`} — {r.payload?.description || '—'}</span>
    )}
    {!isBiometric(r.type) && !r.type.startsWith('letter_') && !['staff_attendance_mark','salary_action','fee_mark_paid','fee_add_fine','fee_discount','fee_edit','fee_bulk_generate','staff_leave_add','staff_leave_review','staff_add','staff_edit','staff_toggle','staff_exit','staff_reactivate','expense_add','expense_edit','expense_delete','asset_add','asset_edit','asset_delete','asset_maintenance'].includes(r.type) && '—'}
  </>
);

export default function FinanceRequestsAdmin() {
  const [requests,    setRequests]    = useState([]);
  const [allCounts,   setAllCounts]   = useState({ pending:0, approved:0, rejected:0 });
  const [loading,     setLoading]     = useState(true);
  const [filter,      setFilter]      = useState('');
  const [typeFilter,  setTypeFilter]  = useState('');
  const [actionModal, setActionModal] = useState(null);
  const [adminNote,   setAdminNote]   = useState('');
  const [saving,      setSaving]      = useState(false);

  useEffect(() => { load(); }, [filter, typeFilter]);

  const load = () => {
    setLoading(true);
    const params = [];
    if (filter)     params.push(`status=${filter}`);
    if (typeFilter) params.push(`type=${typeFilter}`);
    const q = params.length ? '?' + params.join('&') : '';
    Promise.all([
      api.get(`/admin/finance-requests${q}`),
      api.get('/admin/finance-requests'),
    ]).then(([filtered, all]) => {
      setRequests(filtered.data || []);
      const allData = all.data || [];
      setAllCounts({
        pending:  allData.filter(r => r.status === 'pending').length,
        approved: allData.filter(r => r.status === 'approved').length,
        rejected: allData.filter(r => r.status === 'rejected').length,
      });
    }).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  };

  const openAction = (request, action) => { setActionModal({ request, action }); setAdminNote(''); };

  const submitAction = async () => {
    try {
      setSaving(true);
      await api.post(`/admin/finance-requests/${actionModal.request._id}/action`, {
        action: actionModal.action,
        adminNote,
      });
      toast.success(`Request ${actionModal.action}`);
      setActionModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const isBiometric = (type) => BIOMETRIC_TYPES.includes(type);

  return (
    <div className="fra"><style>{css}</style>
      <div className="fra-hdr">
        <div>
          <div className="fra-title">Finance Requests</div>
          <div className="fra-sub">Review and approve or reject finance department requests</div>
        </div>
      </div>

      <div className="stat-grid">
        <div className="stat-card"><div className="sc-label">Pending</div><div className="sc-value" style={{color:'#f59e0b'}}>{allCounts.pending}</div></div>
        <div className="stat-card"><div className="sc-label">Approved</div><div className="sc-value" style={{color:'#10c98e'}}>{allCounts.approved}</div></div>
        <div className="stat-card"><div className="sc-label">Rejected</div><div className="sc-value" style={{color:'#ef4444'}}>{allCounts.rejected}</div></div>
      </div>

      {/* Status tabs */}
      <div className="tabs">
        {['','approved','pending','rejected'].map(s => (
          <button key={s} className={`tab${filter===s?' on':''}`} onClick={() => setFilter(s)}>
            {s || 'All'}
          </button>
        ))}
      </div>

      {/* Type filter */}
      <div style={{display:'flex',gap:8,marginBottom:'1rem',flexWrap:'wrap',alignItems:'center'}}>
        <span style={{fontSize:'.74rem',color:'#9094a8',fontWeight:600}}>Filter by type:</span>
        {[
          { value:'',                   label:'All Types' },
          { value:'biometric_config',   label:'Biometric Config' },
          { value:'rfid_assign_student',label:'RFID Student' },
          { value:'rfid_assign_teacher',label:'RFID Teacher' },
          { value:'staff_attendance_mark',label:'Staff Attendance' },
        ].map(opt => (
          <button key={opt.value}
            style={{padding:'4px 12px',borderRadius:99,border:'1.5px solid',fontSize:'.72rem',fontWeight:600,cursor:'pointer',fontFamily:'Sora,sans-serif',
              borderColor: typeFilter===opt.value ? '#3b6ef8' : '#e0e4f0',
              background:  typeFilter===opt.value ? '#eef2ff'  : '#f8f9fc',
              color:       typeFilter===opt.value ? '#3b6ef8'  : '#6b7194',
            }}
            onClick={() => setTypeFilter(typeFilter===opt.value ? '' : opt.value)}>
            {opt.label}
          </button>
        ))}
      </div>

      <div className="panel">
        {loading
          ? <div className="loading"><div className="spin"/>Loading…</div>
          : !requests.length
          ? <div className="empty">No {filter} requests{typeFilter ? ` of type "${TYPE_LABEL[typeFilter]||typeFilter}"` : ''}.</div>
          : <div className="tbl-wrap" style={{overflowX:'auto'}}>
              <table className="tbl">
                <thead>
                  <tr><th>Type</th><th>Requested By</th><th>Summary</th><th>Note</th><th>Status</th><th>Date</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {requests.map(r => (
                    <tr key={r._id}>
                      <td>
                        <div style={{fontWeight:600,fontSize:'.8rem'}}>{TYPE_LABEL[r.type] || r.type}</div>
                        {isBiometric(r.type) && <span className="biometric-badge">Biometric</span>}
                      </td>
                      <td style={{fontSize:'.78rem'}}>{r.requestedBy?.name || '—'}</td>
                      <td style={{fontSize:'.74rem',color:'#6b7194',maxWidth:180}}>
                        <RequestSummary r={r} isBiometric={isBiometric} />
                      </td>
                      <td style={{fontSize:'.76rem',color:'#6b7194',maxWidth:160}}>{r.note || '—'}</td>
                      <td>
                        <span className={`pill pill-${r.status}`}>{r.status}</span>
                        {r.adminNote && <div style={{fontSize:'.68rem',color:'#9094a8',marginTop:3}}>{r.adminNote}</div>}
                      </td>
                      <td><span className="mono">{fmtDate(r.createdAt)}</span></td>
                      <td>
                        {r.status === 'pending' && (
                          <div style={{display:'flex',gap:6}}>
                            <button className="btn btn-green btn-sm" onClick={() => openAction(r,'approved')}><CheckCircleIcon/>Approve</button>
                            <button className="btn btn-red btn-sm"   onClick={() => openAction(r,'rejected')}><XCircleIcon/>Reject</button>
                          </div>
                        )}
                        {r.status !== 'pending' && (
                          <span style={{fontSize:'.74rem',color:'#9094a8'}}>By {r.resolvedBy?.name || '—'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        }
        {!loading && !!requests.length && (
          <div className="fra-cards">
            {requests.map(r => (
              <div key={r._id} className="fra-card">
                <div className="fra-card-top">
                  <div>
                    <div className="fra-card-type">{TYPE_LABEL[r.type] || r.type}{isBiometric(r.type) && <span className="biometric-badge">Biometric</span>}</div>
                    <div className="fra-card-by">{r.requestedBy?.name || '—'} · {fmtDate(r.createdAt)}</div>
                  </div>
                  <span className={`pill pill-${r.status}`}>{r.status}</span>
                </div>
                <div className="fra-card-row"><span className="fra-card-label">Summary</span><span className="fra-card-val"><RequestSummary r={r} isBiometric={isBiometric} /></span></div>
                {r.note && <div className="fra-card-row"><span className="fra-card-label">Note</span><span className="fra-card-val">{r.note}</span></div>}
                {r.adminNote && <div className="fra-card-row"><span className="fra-card-label">Admin Note</span><span className="fra-card-val">{r.adminNote}</span></div>}
                {r.status !== 'pending' && <div className="fra-card-row"><span className="fra-card-label">Resolved By</span><span className="fra-card-val">{r.resolvedBy?.name || '—'}</span></div>}
                {r.status === 'pending' && (
                  <div className="fra-card-actions">
                    <button className="btn btn-green btn-sm" onClick={() => openAction(r,'approved')}><CheckCircleIcon/>Approve</button>
                    <button className="btn btn-red btn-sm"   onClick={() => openAction(r,'rejected')}><XCircleIcon/>Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Action modal */}
      {actionModal && createPortal(
        <div className="mb" onClick={() => setActionModal(null)}>
          <div className="mo" onClick={e => e.stopPropagation()}>
            <div className="mo-hd">
              <div className="mo-title">{actionModal.action === 'approved' ? '✅ Approve' : '❌ Reject'} Request</div>
              <button className="mo-x" onClick={() => setActionModal(null)}><XMarkIcon/></button>
            </div>
            <div className="mo-bd">
              {/* Type + requester */}
              <div style={{background:'#f8f9fc',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:'.78rem'}}>
                <div style={{fontWeight:700,color:'#0f1123',marginBottom:4}}>
                  {TYPE_LABEL[actionModal.request.type] || actionModal.request.type}
                  {isBiometric(actionModal.request.type) && <span className="biometric-badge">Biometric</span>}
                </div>
                <div style={{color:'#6b7194'}}>From: {actionModal.request.requestedBy?.name}</div>
                {actionModal.request.note && <div style={{color:'#6b7194',marginTop:2}}>Note: {actionModal.request.note}</div>}
              </div>

              {/* Payload */}
              <PayloadView type={actionModal.request.type} payload={actionModal.request.payload} />

              {/* Admin note */}
              <div className="ff">
                <label className="fl">
                  {actionModal.action === 'rejected' ? 'Rejection Reason *' : 'Admin Note (optional)'}
                </label>
                <textarea
                  className="ft"
                  value={adminNote}
                  onChange={e => setAdminNote(e.target.value)}
                  placeholder={actionModal.action === 'rejected' ? 'Why is this being rejected?' : 'Any notes for the finance team…'}
                />
              </div>

              {/* Warning for biometric approvals */}
              {isBiometric(actionModal.request.type) && actionModal.action === 'approved' && (
                <div style={{background:'#ecfdf5',border:'1px solid #a7f3d0',borderRadius:9,padding:'10px 12px',fontSize:'.75rem',color:'#065f46'}}>
                  ✅ On approval this will execute immediately — {
                    actionModal.request.type === 'rfid_assign_teacher' ? 'RFID card will be assigned to the teacher' :
                    actionModal.request.type === 'rfid_assign_student' ? 'RFID card will be assigned to the student' :
                    'Machine config will be updated and polling restarted if active'
                  }
                </div>
              )}
            </div>
            <div className="mo-ft">
              <button className="btn btn-ghost" onClick={() => setActionModal(null)}>Cancel</button>
              <button
                className={`btn ${actionModal.action === 'approved' ? 'btn-green' : 'btn-red'}`}
                onClick={submitAction}
                disabled={saving || (actionModal.action === 'rejected' && !adminNote.trim())}
              >
                {saving ? 'Processing…' : actionModal.action === 'approved' ? 'Approve & Execute' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      
      , document.body)}
    </div>
  );
}